// 2FA (TOTP) codes, computed in the browser.
//
// RFC 6238 on top of RFC 4226: the code is HMAC(secret, counter) where the
// counter is the Unix time divided by the step, dynamically truncated to N
// digits. Web Crypto does the HMAC, so there is no dependency and no upload.
//
// Doing this locally is not a detail, it is the whole point: a 2FA secret is a
// permanent credential. Every "online 2FA code generator" that posts the secret
// to a server has handed over the second factor for good, and the user cannot
// tell from the outside. Ours never leaves the tab.

export type TotpAlgorithm = 'SHA-1' | 'SHA-256' | 'SHA-512';

export type TotpOptions = {
  secret: string;
  digits?: number;
  period?: number;
  algorithm?: TotpAlgorithm;
  /** Unix seconds. Injectable so the same function is testable. */
  now?: number;
};

export type TotpResult = {
  code: string;
  /** Seconds until this code is replaced. */
  secondsRemaining: number;
  period: number;
};

/** What an otpauth:// URI carries, which is what every QR code encodes. */
export type OtpauthParts = {
  secret: string;
  issuer?: string;
  account?: string;
  digits: number;
  period: number;
  algorithm: TotpAlgorithm;
};

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/**
 * Base32 (RFC 4648) without padding, the encoding every authenticator uses for
 * secrets. Written out rather than pulled in: it is 20 lines and a dependency
 * here would be a dependency that sees the secret.
 */
export function decodeBase32(input: string): Uint8Array {
  // Authenticator apps and setup pages print secrets in spaced, lower-case
  // groups ("jbsw y3dp ehpk 3pxp"), so normalising is not optional.
  const cleaned = input.replace(/[\s-]/g, '').replace(/=+$/, '').toUpperCase();

  if (!cleaned) {
    throw new Error('Enter your 2FA secret key.');
  }

  const invalid = cleaned.split('').find((character) => !BASE32_ALPHABET.includes(character));

  if (invalid) {
    throw new Error(
      `"${invalid}" is not valid in a 2FA secret. Base32 keys use A-Z and 2-7 only (no 0, 1, or 8).`,
    );
  }

  const bytes: number[] = [];
  let buffer = 0;
  let bitsInBuffer = 0;

  for (const character of cleaned) {
    buffer = (buffer << 5) | BASE32_ALPHABET.indexOf(character);
    bitsInBuffer += 5;

    if (bitsInBuffer >= 8) {
      bitsInBuffer -= 8;
      bytes.push((buffer >> bitsInBuffer) & 0xff);
    }
  }

  if (!bytes.length) {
    throw new Error('That secret is too short to produce a code.');
  }

  return new Uint8Array(bytes);
}

/** The 8-byte big-endian counter RFC 4226 hashes. */
function counterBytes(counter: number): Uint8Array {
  const bytes = new Uint8Array(8);
  // Bitwise operators are 32-bit in JS and the counter outgrows that in 2038,
  // so the high half is divided out rather than shifted.
  let high = Math.floor(counter / 0x100000000);
  let low = counter % 0x100000000;

  for (let index = 7; index >= 4; index -= 1) {
    bytes[index] = low & 0xff;
    low = Math.floor(low / 256);
  }

  for (let index = 3; index >= 0; index -= 1) {
    bytes[index] = high & 0xff;
    high = Math.floor(high / 256);
  }

  return bytes;
}

export async function generateTotp({
  secret,
  digits = 6,
  period = 30,
  algorithm = 'SHA-1',
  now = Math.floor(Date.now() / 1000),
}: TotpOptions): Promise<TotpResult> {
  if (period <= 0) {
    throw new Error('The period must be a positive number of seconds.');
  }

  if (digits < 6 || digits > 10) {
    throw new Error('Codes are between 6 and 10 digits.');
  }

  const key = decodeBase32(secret);
  const counter = Math.floor(now / period);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: algorithm },
    false,
    ['sign'],
  );
  const signature = new Uint8Array(
    await crypto.subtle.sign('HMAC', cryptoKey, counterBytes(counter) as BufferSource),
  );

  // Dynamic truncation (RFC 4226 §5.3): the low nibble of the last byte picks
  // where to read the 4-byte window from.
  const offset = signature[signature.length - 1] & 0x0f;
  const binary =
    ((signature[offset] & 0x7f) << 24) |
    ((signature[offset + 1] & 0xff) << 16) |
    ((signature[offset + 2] & 0xff) << 8) |
    (signature[offset + 3] & 0xff);

  const code = String(binary % 10 ** digits).padStart(digits, '0');

  return { code, secondsRemaining: period - (now % period), period };
}

/**
 * Parse an otpauth:// URI, which is what a 2FA QR code contains.
 *
 * Accepts a bare secret too: people paste whichever half of the setup screen
 * they managed to copy, and refusing the common case would be pedantry.
 */
export function parseOtpauth(input: string): OtpauthParts {
  const trimmed = input.trim();
  const fallback = { digits: 6, period: 30, algorithm: 'SHA-1' as TotpAlgorithm };

  if (!/^otpauth:\/\//i.test(trimmed)) {
    return { secret: trimmed, ...fallback };
  }

  let url: URL;

  try {
    url = new URL(trimmed);
  } catch {
    throw new Error('That otpauth:// link could not be read.');
  }

  const params = url.searchParams;
  const secret = params.get('secret') || '';

  if (!secret) {
    throw new Error('That otpauth:// link carries no secret.');
  }

  // The label is "Issuer:account" percent-encoded after the type segment.
  const label = decodeURIComponent(url.pathname.replace(/^\/+/, '')).replace(/^totp\//i, '');
  const [labelIssuer, labelAccount] = label.includes(':') ? label.split(/:(.+)/) : ['', label];

  const rawAlgorithm = (params.get('algorithm') || 'SHA1').toUpperCase().replace('SHA', 'SHA-');
  const algorithm: TotpAlgorithm =
    rawAlgorithm === 'SHA-256' || rawAlgorithm === 'SHA-512' ? rawAlgorithm : 'SHA-1';

  return {
    secret,
    issuer: params.get('issuer') || labelIssuer || undefined,
    account: labelAccount || undefined,
    digits: Number(params.get('digits')) || 6,
    period: Number(params.get('period')) || 30,
    algorithm,
  };
}

/** Build the otpauth:// URI a QR code should encode, for the setup direction. */
export function buildOtpauth({
  secret,
  issuer,
  account,
  digits = 6,
  period = 30,
  algorithm = 'SHA-1',
}: {
  secret: string;
  issuer?: string;
  account?: string;
  digits?: number;
  period?: number;
  algorithm?: TotpAlgorithm;
}) {
  // Validate before building: a QR that encodes an unusable secret is worse
  // than an error, because it fails later, inside the authenticator app.
  decodeBase32(secret);

  const label = [issuer, account].filter(Boolean).join(':') || 'account';
  const params = new URLSearchParams({
    secret: secret.replace(/[\s-]/g, '').toUpperCase(),
    algorithm: algorithm.replace('-', ''),
    digits: String(digits),
    period: String(period),
  });

  if (issuer) {
    params.set('issuer', issuer);
  }

  return `otpauth://totp/${encodeURIComponent(label)}?${params.toString()}`;
}

/** A fresh random Base32 secret, for the setup direction. */
export function generateSecret(bytes = 20) {
  const random = new Uint8Array(bytes);
  crypto.getRandomValues(random);

  let bits = 0;
  let value = 0;
  let output = '';

  for (const byte of random) {
    value = (value << 8) | byte;
    bits += 8;

    while (bits >= 5) {
      bits -= 5;
      output += BASE32_ALPHABET[(value >>> bits) & 31];
    }
  }

  if (bits > 0) {
    output += BASE32_ALPHABET[(value << (5 - bits)) & 31];
  }

  return output;
}
